import { LopDetailNote } from "./LopDetailNote";
import { useLocale } from "@/hooks/useLocale";
import { useTranslation } from "react-i18next";
import type { IPayslipPreview } from "@/types/(company)/payrollRuns.interface";
import { formatDateDmy } from "@/utils/helper";
import { PayslipPeriodRows } from "./PayslipPeriodRows";

const toNumber = (v: string | number | null | undefined): number => {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "string" ? Number(v.replace(/,/g, "")) : v;
  return Number.isFinite(n) ? n : 0;
};

/** Amounts and dates go through `useLocale` (see CLAUDE.md) so Arabic gets
 *  the right digit grouping and month names — `toLocaleString(undefined, …)`
 *  followed the *browser* locale, not the app's. */

const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[9px] uppercase tracking-wider text-gray-400">
      {label}
    </span>
    <span className="text-[11px] font-semibold text-gray-900">{value}</span>
  </div>
);

export const PayslipPreviewCard = ({
  preview,
}: {
  preview: IPayslipPreview;
}) => {
  const { t } = useTranslation(["common", "company", "my-space"]);
  const { formatNumber: fmtNumber, formatDate: fmtDate } = useLocale();

  const money = (n: number) =>
    fmtNumber(n, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const safeFormat = (iso: string | null | undefined, pattern: string) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "—" : fmtDate(d, pattern);
  };
  const { company, employee, currency, earnings, deductions } = preview;
  // Total Earnings already includes benefits; list them so the rows add up
  // to it (B41).
  const earningRows = [...earnings, ...(preview.benefits ?? [])];
  const logoUrl = company.logo?.trim() || null;

  return (
    <div
      className="rounded-xs border border-borderSecondary bg-white text-gray-700 flex flex-col overflow-hidden"
      style={{ aspectRatio: "210/297", minHeight: "640px" }}
    >
      {/* Brand strip */}
      <div className="flex items-center justify-between px-5 py-4">
        <div className="flex items-center gap-2">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={company.name}
              className="h-9 max-w-[165px] object-contain"
            />
          ) : (
            <span className="text-sm font-bold text-gray-900">
              {company.name}
            </span>
          )}
        </div>
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">
          {t("company:payslip.payslip")}
        </span>
      </div>

      <div className="h-px bg-gradient-to-r from-pink-400 via-pink-500 to-rose-500" />

      {/* Company + payslip details */}
      <div className="grid grid-cols-2 gap-6 bg-gray-50 px-5 py-4">
        <div className="flex flex-col gap-0.5">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">
            {t("company:payslip.company")}
          </span>
          <span className="text-sm font-bold text-gray-900">
            {company.name}
          </span>
          {company.address && (
            <span className="text-[11px] text-gray-500">
              {typeof company.address === "string"
                ? company.address
                : [
                    (company.address as any).street,
                    (company.address as any).city,
                    (company.address as any).state,
                  ]
                    .filter(Boolean)
                    .join(", ")}
            </span>
          )}
          {(company.email || company.phone) && (
            <span className="text-[11px] text-gray-500">
              {company.email}
              {company.email && company.phone && "  |  "}
              {company.phone}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-1 text-end">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">
            {t("company:payslip.payslip_details")}
          </span>
          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px]">
            <span className="text-gray-500 text-start">
              {t("company:payslip.pay_period_colon")}
            </span>
            <span className="font-semibold text-gray-900 text-end">
              {preview.payPeriod}
            </span>
            <PayslipPeriodRows preview={preview} />
            <span className="text-gray-500 text-start">
              {t("company:payslip.payment_date_colon")}
            </span>
            <span className="font-semibold text-gray-900 text-end">
              {safeFormat(preview.paymentDate, "dd MMM yyyy")}
            </span>
            <span className="text-gray-500 text-start">
              {t("company:payslip.payslip_no_colon")}
            </span>
            <span className="font-semibold text-gray-900 text-end">
              {preview.payslipNo}
            </span>
          </div>
        </div>
      </div>

      {/* Employee info */}
      <div className="px-5 pt-4 pb-4">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">
          {t("company:payslip.employee_information")}
        </span>
        <div className="grid grid-cols-4 gap-x-4 gap-y-3 mt-3">
          <Field
            label={t("my-space:ui.employee_name")}
            value={employee.fullName}
          />
          <Field
            label={t("my-space:settings.employee_id")}
            value={employee.employeeCode}
          />
          <Field
            label={t("common:chrome.designation")}
            value={employee.designation ?? "—"}
          />
          <Field
            label={t("common:labels.department")}
            value={employee.department ?? "—"}
          />
          <Field
            label={t("my-space:settings.joining_date")}
            value={safeFormat(employee.joiningDate, "dd MMM yyyy")}
          />
          <Field
            label={t("company:payrolls.payment_mode")}
            value={preview.paymentMode ?? "—"}
          />
          <Field
            label={t("my-space:payroll.bank_name")}
            value={employee.bankName ?? "—"}
          />
          <Field
            label={t("company:payrolls.bank_a_c_no")}
            value={
              <span className="truncate block">
                {employee.accountNumber ?? "—"}
              </span>
            }
          />
        </div>
      </div>

      {/* Earnings + deductions tables */}
      <div className="grid grid-cols-2 gap-3 px-5">
        <div className="rounded-xs border border-borderSecondary overflow-hidden flex flex-col">
          <div className="flex items-center justify-between bg-gray-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            <span>{t("company:payslip.earnings")}</span>
            <span>
              {t("company:payslip.amount_currency", {
                currency: currency?.code,
              })}
            </span>
          </div>
          <div className="flex flex-col flex-1">
            {earningRows.length === 0 ? (
              <span className="px-3 py-2 text-[11px] text-gray-400">
                {t("company:payslip.no_earnings")}
              </span>
            ) : (
              earningRows.map((e, idx) => (
                <div
                  key={`${e.name}-${idx}`}
                  className="flex items-center justify-between gap-2 px-3 py-1.5 text-[11px]"
                >
                  <span className="truncate text-gray-700">{e.name}</span>
                  <span className="font-semibold text-gray-900">
                    {money(toNumber(e.amount))}
                  </span>
                </div>
              ))
            )}
          </div>
          <div className="flex items-center justify-between border-t border-borderSecondary px-3 py-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
              {t("company:payslip.total_earnings")}
            </span>
            <span className="text-[11px] font-bold text-primary">
              {money(preview.totalEarnings)}
            </span>
          </div>
        </div>

        <div className="rounded-xs border border-borderSecondary overflow-hidden flex flex-col">
          <div className="flex items-center justify-between bg-gray-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            <span>{t("company:payslip.deductions")}</span>
            <span>
              {t("company:payslip.amount_currency", {
                currency: currency?.code,
              })}
            </span>
          </div>
          <div className="flex flex-col flex-1">
            {deductions.length === 0 ? (
              <span className="px-3 py-2 text-[11px] text-gray-400">
                {t("company:payslip.no_deductions")}
              </span>
            ) : (
              deductions.map((d, idx) => (
                <div
                  key={`${d.name}-${idx}`}
                  className="flex items-center justify-between gap-2 px-3 py-1.5 text-[11px]"
                >
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-gray-700">
                      {/* The server names Loss of Pay in English (B45). */}
                      {d.lopDetail ? t("company:payrolls.loss_of_pay") : d.name}
                    </span>
                    <LopDetailNote detail={d.lopDetail} />
                  </span>
                  <span className="font-semibold text-gray-900">
                    {money(toNumber(d.amount))}
                  </span>
                </div>
              ))
            )}
          </div>
          <div className="flex items-center justify-between border-t border-borderSecondary px-3 py-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
              {t("company:payslip.total_deductions")}
            </span>
            <span className="text-[11px] font-bold text-red-500">
              {money(preview.totalDeductions)}
            </span>
          </div>
        </div>
      </div>

      <div className="flex-1" />

      {/* Net salary */}
      <div className="bg-gray-50 px-5 py-4 flex items-center justify-between border-t border-borderSecondary mt-4">
        <div className="flex flex-col">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-700">
            {t("company:payslip.net_salary")}
          </span>
          <span className="text-[10px] text-gray-500">
            {t("company:payslip.take_home_after_deductions")}
          </span>
        </div>
        <div className="flex flex-col items-end">
          <span className="text-[10px] font-semibold uppercase text-gray-400">
            {currency?.code}
          </span>
          <span className="text-xl font-bold text-gray-900">
            {money(preview.netSalary)}
          </span>
        </div>
      </div>

      {/* Signatures */}
      <div className="grid grid-cols-2 gap-6 px-5 pt-6 pb-4">
        <div className="flex flex-col">
          <div className="h-7 border-b border-gray-300" />
          <span className="text-[11px] text-gray-500 mt-1">
            {t("company:payslip.employer_signature")}
          </span>
        </div>
        <div className="flex flex-col">
          <div className="h-7 border-b border-gray-300" />
          <span className="text-[11px] text-gray-500 mt-1">
            {t("company:payslip.employee_signature")}
          </span>
        </div>
      </div>

      <div className="h-px bg-gradient-to-r from-primary/40 via-primary to-primary/40" />
      <div className="px-5 py-2 text-center text-[10px] text-gray-500">
        {t("company:payslip.company_generated_on", {
          company: company.name,
          date: formatDateDmy(new Date()),
        })}
      </div>
    </div>
  );
};
